import type { ReactNode } from "react";

interface FieldProps {
  label: string;
  htmlFor?: string | undefined;
  /** Names the fix, never the fault. Rendered inline, under the input. */
  error?: string | null | undefined;
  hint?: string | undefined;
  children: ReactNode;
}

/**
 * Label + input + inline error.
 *
 * The error sits at the field rather than in a banner, because a banner makes
 * the reader hunt for which field it means. It is wired through `aria-describedby`
 * at the call site so a screen reader announces the fix with the field, not
 * as a separate region the user has to go find.
 */
export function Field({ label, htmlFor, error, hint, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-[7px]">
      <label
        htmlFor={htmlFor}
        className="text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted"
      >
        {label}
      </label>
      {children}
      {error ? (
        <p id={htmlFor ? `${htmlFor}-error` : undefined} className="text-[12.5px] text-negative">
          {error}
        </p>
      ) : hint ? (
        <p className="text-[12.5px] text-text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

/** Shared shell so every input in the flow has the same box. */
export const inputShell =
  "flex items-center gap-2.5 bg-surface border rounded-[var(--radius-control)] px-3.5 py-3.5 min-h-12";
